import axios from "../../api/axiosInstance";
import { STATUS, getProjectId, extractErrorInfo } from "./sharedfunction";

/**
 * Get all available prompts for AI test case generation
 * @returns {Promise<{status: number, data?: Array, error?: string, message?: string}>}
 */
export async function getPrompts() {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/${projectId}/ai/prompts/get`,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || [],
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error fetching prompts:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Generate test cases using AI
 * @param {Object} params - Generation parameters
 * @param {string} params.input - Input description or requirements
 * @param {string} [params.additionalPrompt] - Additional instructions or context
 * @param {Array<string>} [params.types] - Array of test case types
 * @param {number} params.count - Number of test cases to generate (1-100)
 * @param {boolean} [params.isIncludeTestdata] - Whether to include test data
 * @param {string} [params.LLMId] - Optional legacy UUID (server resolves org AI Generator mapping when omitted)
 * @returns {Promise<{status: number, data?: Object, error?: string, message?: string}>}
 */
export async function generateTestCases({
  input,
  additionalPrompt,
  types,
  count,
  isIncludeTestdata = false,
  LLMId,
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    // Validate required parameters
    if (!count || count < 1 || count > 100) {
      return {
        status: STATUS.ERROR,
        error: "Count must be between 1 and 100",
      };
    }

    const requestBody = {
      count,
      isIncludeTestdata,
    };
    if (LLMId) {
      requestBody.LLMId = LLMId;
    }

    // Add optional parameters if provided
    if (input) {
      requestBody.input = input;
    }

    if (additionalPrompt) {
      requestBody.additionalPrompt = additionalPrompt;
    }

    if (types && Array.isArray(types) && types.length > 0) {
      requestBody.types = types;
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/ai/generateTestCase`,
      requestBody,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error generating test cases:", error);
    return extractErrorInfo(error);
  }
}

/**
 * Generate improved test cases with additional context
 * @param {Object} params - Improvement parameters
 * @param {string} params.generatedTestScenario - Previously generated test scenario
 * @param {string} [params.additionalContext] - Additional context for improvement
 * @param {string} params.type - Type of test case
 * @param {string} params.LLMId - UUID of the LLM model to use
 * @returns {Promise<{status: number, data?: Object, error?: string, message?: string}>}
 */
export async function generateImprovedTestCases({
  generatedTestScenario,
  additionalContext,
  type,
  LLMId,
} = {}) {
  try {
    const projectId = getProjectId();
    
    if (!projectId) {
      return {
        status: STATUS.ERROR,
        error: "Project ID is required",
      };
    }

    // Validate required parameters
    if (!generatedTestScenario || generatedTestScenario.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Generated test scenario is required",
      };
    }

    if (!type || type.trim().length === 0) {
      return {
        status: STATUS.ERROR,
        error: "Type is required",
      };
    }

    if (!LLMId) {
      return {
        status: STATUS.ERROR,
        error: "LLM ID is required",
      };
    }

    const requestBody = {
      generatedTestScenario,
      type,
      LLMId,
    };

    // Add optional parameter if provided
    if (additionalContext) {
      requestBody.additionalContext = additionalContext;
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/${projectId}/ai/generateImprovedTestCase`,
      requestBody,
      {
        withCredentials: true,
      }
    );

    if (response?.data?.success) {
      return {
        status: STATUS.SUCCESS,
        data: response?.data?.data || {},
        message: response?.data?.message,
      };
    }

    return {
      status: STATUS.ERROR,
      error: response?.data?.message || response?.data?.error,
      errors: response?.data?.errors,
    };
  } catch (error) {
    console.error("Error generating improved test cases:", error);
    return extractErrorInfo(error);
  }
}

